import { useVirtualizer } from "@tanstack/react-virtual"
import { useContext, useEffect, useMemo, useRef } from "react"

import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { WithID } from "shared/types/WithID"

import { TableAPIContext } from "../TableAPIContext"
import { useTableDnD } from "../dn-d/useTableDnD"
import { TableAPI, TableContextType, TableProps } from "../types"
import { useTableKeyboardControls } from "./useTableKeyboardControls"
import { useTableSelect } from "./useTableSelect"

export function useTableViewModel<I extends WithID>(props: TableProps<I>) {
  const {
    isReadonly = false,
    rowHeight = 0,
    maxDepth = 3,
    selectedItems = [],
    multiSelect,
    additiveSelect,
    rowFocusOnly,
    isDragDisabled = false,
    hideDefaultDragPreview = false,
    onSelectedItemsChange,
    RowRenderer,
    isItemCollapsed,
    onItemCollapse,
    getRowCells,
    topOffset = 0,
    bottomOffset = 0,
    tableAPIRef,
    getDepth,
    getDragType,
    canDrag
  } = props
  const { tableAPIRef: contextAPIRef } = useContext(TableAPIContext)
  const { flattenedTree, dndContext, dragMoveContext } = useTableDnD(props)

  const listContainerRef = useRef<HTMLDivElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const scrollElementRef = useRef<string | null>(null)

  const onFocusTable = useEvent(() => {
    tableContainerRef.current?.focus({ preventScroll: true })
  })

  const scrollToIndex = useEvent((index: number) => {
    const container = listContainerRef.current
    if (!container) return

    const rowTop = index * rowHeight
    const rowBottom = rowTop + rowHeight
    const { scrollTop, clientHeight } = container

    if (rowTop < scrollTop + topOffset) {
      container.scrollTo({
        top: Math.ceil(rowTop - topOffset),
        behavior: "instant" as ScrollBehavior
      })
    } else if (rowBottom > scrollTop + clientHeight - bottomOffset) {
      container.scrollTo({
        top: Math.floor(rowBottom - clientHeight + bottomOffset),
        behavior: "instant" as ScrollBehavior
      })
    }
  })

  const { onSelect, onMultiSelect, isItemSelected } = useTableSelect({
    flattenedTree,
    selectedItems,
    multiSelect,
    additiveSelect,
    onSelectedItemsChange
  })

  const {
    isTableFocused,
    focusedRow,
    focusedCell,
    focusableElement,
    setFocusedID,
    onBlur,
    onFocus,
    onKeyDown,
    onRowFocus,
    setFocusableElement
  } = useTableKeyboardControls({
    items: flattenedTree,
    containerRef: tableContainerRef,
    isItemCollapsed,
    onItemCollapse,
    getRowCells,
    topOffset,
    bottomOffset,
    onFocusTable,
    scrollToIndex,
    selectItem: onSelect,
    selectItems: onMultiSelect,
    rowFocusOnly
  })

  const virtualizer = useVirtualizer({
    count: flattenedTree.length + (isDragDisabled ? 0 : 1),
    getScrollElement: () => listContainerRef.current,
    estimateSize: () => rowHeight || 0,
    overscan: 3
  })

  const scrollToElement = useEvent((id: string) => {
    const index = flattenedTree.findIndex(node => node.id === id)

    if (index !== -1) {
      scrollToIndex(index)
    } else {
      scrollElementRef.current = id
    }
  })

  useEffect(() => {
    if (scrollElementRef.current) {
      const index = flattenedTree.findIndex(
        node => node.id === scrollElementRef.current
      )
      if (index !== -1) {
        scrollElementRef.current = null
        scrollToIndex(index)
      }
    }
  }, [flattenedTree])

  const tableAPI: TableAPI = {
    scrollToElement,
    setFocusableElement: setFocusedID
  }

  const mergedAPIRef = useMergedRefs(tableAPIRef, contextAPIRef)

  useEffect(() => {
    mergedAPIRef(tableAPI)
  })

  const tableContext = useMemo(() => {
    const tableContextValue: TableContextType<I> = {
      isReadonly,
      isTableFocused,
      focusedRow,
      focusedCell,
      setFocusableElement,
      onRowFocus,
      rowHeight,
      maxDepth,
      topOffset,
      bottomOffset,
      isDragDisabled,
      hideDefaultDragPreview,
      RowRenderer,
      isItemCollapsed,
      getDepth,
      getDragType,
      canDrag,
      onItemCollapse,
      focusTable: onFocusTable,
      onSelect,
      isItemSelected,
      getRowCells
    }
    return tableContextValue
  }, [
    isReadonly,
    isTableFocused,
    rowHeight,
    maxDepth,
    focusedRow,
    focusedCell,
    isDragDisabled,
    hideDefaultDragPreview,
    isItemCollapsed,
    getRowCells,
    getDepth,
    getDragType,
    canDrag,
    onSelect,
    isItemSelected,
    onRowFocus
  ])

  return {
    tableContainerRef,
    listContainerRef,
    tableCanReceiveFocus: !focusableElement && flattenedTree.length > 0,
    onFocus,
    onBlur,
    virtualizer,
    rowHeight,
    flattenedTree,
    dndContext,
    dragMoveContext,
    tableContext,
    onKeyDown
  }
}
